import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';

import getInjectors from './sagaInjectors';

// Global store reference - will be set by app.js
let globalStore = null;

export function setStore(store) {
  globalStore = store;
  console.log('[injectSaga] Store initialized');
}

/**
 * Dynamically injects a saga, passes component's props as saga arguments
 *
 * @param {string} key A key of the saga
 * @param {function} saga A root saga that will be injected
 * @param {string} [mode] By default (constants.RESTART_ON_REMOUNT) the saga will be started on component mount and
 * cancelled with `task.cancel()` on component un-mount for improved performance. Another two options:
 *   - constants.DAEMON—starts the saga on component mount and never cancels it or starts again,
 *   - constants.ONCE_TILL_UNMOUNT—behaves like 'RESTART_ON_REMOUNT' but never runs it again.
 *
 */
export default ({ key, saga, mode }) => WrappedComponent => {
  function InjectSaga(props) {
    const injectorsRef = useRef(null);

    // Inject once on mount; do not reinject/eject on every render
    useEffect(() => {
      if (!injectorsRef.current && globalStore) {
        injectorsRef.current = getInjectors(globalStore);
        const { injectSaga: inject } = injectorsRef.current;
        inject(key, { saga, mode }, props);
      }

      return () => {
        if (injectorsRef.current) {
          const { ejectSaga } = injectorsRef.current;
          ejectSaga(key);
        }
      };
    }, []);

    return <WrappedComponent {...props} />;
  }

  InjectSaga.displayName = `withSaga(${WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component'})`;
  InjectSaga.WrappedComponent = WrappedComponent;

  return hoistNonReactStatics(InjectSaga, WrappedComponent);
};
